/* OMNIMUSCLE BodyDiagram — Bold athletic icon style
   Each gender + view has hand-drawn SVG paths with anatomical shapes.
   Muscle regions are layered ON TOP of the silhouette and recolored from heat-map.
*/
import React from "react";

export const MUSCLE_LABELS = {
  chest: "Chest",
  shoulders: "Shoulders",
  biceps: "Biceps",
  forearms: "Forearms",
  abs: "Abs / Core",
  quads: "Quads",
  calves: "Calves",
  traps: "Traps",
  lats: "Lats / Back",
  triceps: "Triceps",
  "lower-back": "Lower Back",
  glutes: "Glutes",
  hamstrings: "Hamstrings",
};

function HoverLabel({ id }) {
  if (!id) return null;
  return (
    <g pointerEvents="none">
      <rect x="80" y="592" width="140" height="22" fill="#D4AF37" />
      <text
        x="150"
        y="608"
        textAnchor="middle"
        fontSize="13"
        fontWeight="700"
        fill="#000"
        fontFamily="Outfit, sans-serif"
      >
        {MUSCLE_LABELS[id]}
      </text>
    </g>
  );
}

/* helper to create muscle region props */
function muscleProps({ id, view, weekly, hover, setHover, onPick, heatColor }) {
  const count = weekly[id] || 0;
  return {
    fill: heatColor(count),
    stroke: hover === id ? "#D4AF37" : "#000",
    strokeWidth: hover === id ? 2.5 : 1.5,
    cursor: "pointer",
    onMouseEnter: () => setHover(id),
    onMouseLeave: () => setHover(null),
    onClick: () => onPick(id),
    "data-testid": `muscle-${id}-${view}`,
  };
}

/* --------------- MALE FRONT --------------- */
export function MaleFront(props) {
  const { hover } = props;
  const m = (id) => muscleProps({ ...props, id, view: "front" });

  return (
    <svg viewBox="0 0 300 620" className="w-full h-auto">
      {/* SILHOUETTE - dark fill with gold outline */}
      <path
        fill="#0A0A0A"
        stroke="#D4AF37"
        strokeWidth="2.5"
        strokeLinejoin="round"
        d="M 150 12
           C 178 12 195 30 195 60
           C 195 80 188 96 178 102
           L 178 110
           C 195 113 215 118 232 130
           C 252 144 264 165 268 192
           C 270 210 264 230 254 240
           C 248 246 240 250 232 250
           L 234 290
           C 234 310 230 322 222 330
           C 215 336 208 338 200 336
           L 195 320
           L 192 348
           C 195 380 196 415 195 460
           C 194 495 190 530 188 560
           C 187 580 192 596 200 605
           L 175 605
           L 165 470
           L 155 605
           L 145 605
           L 135 605
           L 125 470
           L 115 605
           L 90 605
           C 98 596 103 580 102 560
           C 100 530 96 495 95 460
           C 94 415 95 380 98 348
           L 95 320
           L 90 336
           C 82 338 75 336 68 330
           C 60 322 56 310 56 290
           L 58 250
           C 50 250 42 246 36 240
           C 26 230 20 210 22 192
           C 26 165 38 144 58 130
           C 75 118 95 113 112 110
           L 112 102
           C 102 96 95 80 95 60
           C 95 30 122 12 150 12 Z"
      />

      {/* TRAPS / Neck wedge */}
      <path
        {...m("traps")}
        d="M 130 105 Q 150 100 170 105 L 175 130 Q 150 122 125 130 Z"
      />

      {/* SHOULDERS / DELTS */}
      <path
        {...m("shoulders")}
        d="M 113 116 Q 90 122 72 134 Q 60 146 58 168 Q 70 158 95 152 Q 110 145 118 138 Z"
      />
      <path
        {...m("shoulders")}
        d="M 187 116 Q 210 122 228 134 Q 240 146 242 168 Q 230 158 205 152 Q 190 145 182 138 Z"
      />

      {/* CHEST - two pec shapes */}
      <path
        {...m("chest")}
        d="M 148 128 Q 122 134 112 152 Q 108 175 116 195 Q 132 200 148 196 Z"
      />
      <path
        {...m("chest")}
        d="M 152 128 Q 178 134 188 152 Q 192 175 184 195 Q 168 200 152 196 Z"
      />

      {/* BICEPS */}
      <path
        {...m("biceps")}
        d="M 50 175 Q 38 195 36 222 Q 38 240 46 252 Q 56 240 58 220 Q 58 196 54 178 Z"
      />
      <path
        {...m("biceps")}
        d="M 250 175 Q 262 195 264 222 Q 262 240 254 252 Q 244 240 242 220 Q 242 196 246 178 Z"
      />

      {/* FOREARMS */}
      <path
        {...m("forearms")}
        d="M 46 252 Q 40 280 42 305 Q 50 320 60 322 Q 65 295 60 270 Z"
      />
      <path
        {...m("forearms")}
        d="M 254 252 Q 260 280 258 305 Q 250 320 240 322 Q 235 295 240 270 Z"
      />

      {/* ABS - 6-pack rectangle with subtle dividers */}
      <path
        {...m("abs")}
        d="M 122 200 Q 150 196 178 200 L 178 285 Q 165 295 150 295 Q 135 295 122 285 Z"
      />
      {/* ab divider lines (decorative, no click) */}
      <g pointerEvents="none" stroke="#000" strokeWidth="1" opacity="0.45">
        <line x1="150" y1="208" x2="150" y2="285" />
        <line x1="125" y1="225" x2="175" y2="225" />
        <line x1="125" y1="248" x2="175" y2="248" />
        <line x1="128" y1="270" x2="172" y2="270" />
      </g>

      {/* QUADS */}
      <path
        {...m("quads")}
        d="M 102 348 Q 92 380 95 425 Q 100 460 110 470 Q 122 460 125 425 Q 124 385 118 350 Z"
      />
      <path
        {...m("quads")}
        d="M 198 348 Q 208 380 205 425 Q 200 460 190 470 Q 178 460 175 425 Q 176 385 182 350 Z"
      />

      {/* CALVES */}
      <path
        {...m("calves")}
        d="M 110 470 Q 100 510 105 555 Q 115 595 125 600 Q 130 555 128 510 Q 122 480 116 470 Z"
      />
      <path
        {...m("calves")}
        d="M 190 470 Q 200 510 195 555 Q 185 595 175 600 Q 170 555 172 510 Q 178 480 184 470 Z"
      />

      <HoverLabel id={hover} />
    </svg>
  );
}

/* --------------- MALE BACK --------------- */
export function MaleBack(props) {
  const { hover } = props;
  const m = (id) => muscleProps({ ...props, id, view: "back" });

  return (
    <svg viewBox="0 0 300 620" className="w-full h-auto">
      {/* Same silhouette path */}
      <path
        fill="#0A0A0A"
        stroke="#D4AF37"
        strokeWidth="2.5"
        strokeLinejoin="round"
        d="M 150 12
           C 178 12 195 30 195 60
           C 195 80 188 96 178 102
           L 178 110
           C 195 113 215 118 232 130
           C 252 144 264 165 268 192
           C 270 210 264 230 254 240
           C 248 246 240 250 232 250
           L 234 290
           C 234 310 230 322 222 330
           C 215 336 208 338 200 336
           L 195 320
           L 192 348
           C 195 380 196 415 195 460
           C 194 495 190 530 188 560
           C 187 580 192 596 200 605
           L 175 605
           L 165 470
           L 155 605
           L 145 605
           L 135 605
           L 125 470
           L 115 605
           L 90 605
           C 98 596 103 580 102 560
           C 100 530 96 495 95 460
           C 94 415 95 380 98 348
           L 95 320
           L 90 336
           C 82 338 75 336 68 330
           C 60 322 56 310 56 290
           L 58 250
           C 50 250 42 246 36 240
           C 26 230 20 210 22 192
           C 26 165 38 144 58 130
           C 75 118 95 113 112 110
           L 112 102
           C 102 96 95 80 95 60
           C 95 30 122 12 150 12 Z"
      />

      {/* TRAPS — large diamond between shoulders */}
      <path
        {...m("traps")}
        d="M 150 108 L 200 130 Q 175 165 150 168 Q 125 165 100 130 Z"
      />

      {/* REAR DELTS (shoulders) */}
      <path
        {...m("shoulders")}
        d="M 113 116 Q 90 122 72 134 Q 60 146 58 168 Q 70 158 95 152 Q 110 145 118 138 Z"
      />
      <path
        {...m("shoulders")}
        d="M 187 116 Q 210 122 228 134 Q 240 146 242 168 Q 230 158 205 152 Q 190 145 182 138 Z"
      />

      {/* LATS — V-taper wings */}
      <path
        {...m("lats")}
        d="M 124 145 Q 105 175 100 220 Q 100 260 122 285 L 145 295 L 145 145 Z"
      />
      <path
        {...m("lats")}
        d="M 176 145 Q 195 175 200 220 Q 200 260 178 285 L 155 295 L 155 145 Z"
      />

      {/* TRICEPS - back of upper arm */}
      <path
        {...m("triceps")}
        d="M 50 175 Q 38 195 36 222 Q 38 240 46 252 Q 56 240 58 220 Q 58 196 54 178 Z"
      />
      <path
        {...m("triceps")}
        d="M 250 175 Q 262 195 264 222 Q 262 240 254 252 Q 244 240 242 220 Q 242 196 246 178 Z"
      />

      {/* FOREARMS */}
      <path
        {...m("forearms")}
        d="M 46 252 Q 40 280 42 305 Q 50 320 60 322 Q 65 295 60 270 Z"
      />
      <path
        {...m("forearms")}
        d="M 254 252 Q 260 280 258 305 Q 250 320 240 322 Q 235 295 240 270 Z"
      />

      {/* LOWER BACK */}
      <path
        {...m("lower-back")}
        d="M 122 280 Q 150 285 178 280 L 175 318 Q 150 322 125 318 Z"
      />

      {/* GLUTES */}
      <path
        {...m("glutes")}
        d="M 150 322 Q 122 325 105 340 Q 100 365 115 380 Q 135 385 148 372 Z"
      />
      <path
        {...m("glutes")}
        d="M 150 322 Q 178 325 195 340 Q 200 365 185 380 Q 165 385 152 372 Z"
      />

      {/* HAMSTRINGS */}
      <path
        {...m("hamstrings")}
        d="M 105 385 Q 95 415 95 450 Q 100 462 110 470 Q 122 460 125 425 Q 124 395 120 385 Z"
      />
      <path
        {...m("hamstrings")}
        d="M 195 385 Q 205 415 205 450 Q 200 462 190 470 Q 178 460 175 425 Q 176 395 180 385 Z"
      />

      {/* CALVES */}
      <path
        {...m("calves")}
        d="M 110 470 Q 100 510 105 555 Q 115 595 125 600 Q 130 555 128 510 Q 122 480 116 470 Z"
      />
      <path
        {...m("calves")}
        d="M 190 470 Q 200 510 195 555 Q 185 595 175 600 Q 170 555 172 510 Q 178 480 184 470 Z"
      />

      <HoverLabel id={hover} />
    </svg>
  );
}

/* --------------- FEMALE FRONT --------------- */
export function FemaleFront(props) {
  const { hover } = props;
  const m = (id) => muscleProps({ ...props, id, view: "front" });

  return (
    <svg viewBox="0 0 300 620" className="w-full h-auto">
      {/* Female silhouette: narrower shoulders, defined waist, wider hips */}
      <path
        fill="#0A0A0A"
        stroke="#D4AF37"
        strokeWidth="2.5"
        strokeLinejoin="round"
        d="M 150 12
           C 175 12 192 30 192 58
           C 192 78 184 96 174 102
           L 174 112
           C 188 116 205 122 220 134
           C 235 146 244 164 246 188
           C 247 205 242 222 234 232
           C 228 240 220 244 212 244
           L 214 285
           C 214 305 210 318 202 324
           C 196 330 188 332 182 330
           L 178 314
           L 175 340
           Q 175 355 188 365
           Q 215 375 220 405
           Q 218 442 210 480
           Q 200 510 197 545
           Q 195 580 200 605
           L 175 605
           L 165 470
           L 155 605
           L 145 605
           L 135 605
           L 125 470
           L 115 605
           L 90 605
           Q 95 580 93 545
           Q 90 510 80 480
           Q 72 442 70 405
           Q 75 375 102 365
           Q 115 355 115 340
           L 112 314
           L 108 330
           C 102 332 94 330 88 324
           C 80 318 76 305 76 285
           L 78 244
           C 70 244 62 240 56 232
           C 48 222 43 205 44 188
           C 46 164 55 146 70 134
           C 85 122 102 116 116 112
           L 116 102
           C 106 96 98 78 98 58
           C 98 30 125 12 150 12 Z"
      />

      {/* TRAPS */}
      <path
        {...m("traps")}
        d="M 132 105 Q 150 102 168 105 L 172 128 Q 150 122 128 128 Z"
      />

      {/* SHOULDERS */}
      <path
        {...m("shoulders")}
        d="M 117 118 Q 98 124 82 138 Q 72 150 70 168 Q 82 158 102 152 Q 114 146 122 140 Z"
      />
      <path
        {...m("shoulders")}
        d="M 183 118 Q 202 124 218 138 Q 228 150 230 168 Q 218 158 198 152 Q 186 146 178 140 Z"
      />

      {/* CHEST (women - subtler curve) */}
      <path
        {...m("chest")}
        d="M 148 130 Q 126 138 118 158 Q 116 178 124 192 Q 138 198 148 192 Z"
      />
      <path
        {...m("chest")}
        d="M 152 130 Q 174 138 182 158 Q 184 178 176 192 Q 162 198 152 192 Z"
      />

      {/* BICEPS */}
      <path
        {...m("biceps")}
        d="M 60 175 Q 50 195 48 220 Q 50 238 58 248 Q 66 238 68 218 Q 68 196 64 180 Z"
      />
      <path
        {...m("biceps")}
        d="M 240 175 Q 250 195 252 220 Q 250 238 242 248 Q 234 238 232 218 Q 232 196 236 180 Z"
      />

      {/* FOREARMS */}
      <path
        {...m("forearms")}
        d="M 58 248 Q 52 275 54 298 Q 60 312 70 314 Q 74 290 70 268 Z"
      />
      <path
        {...m("forearms")}
        d="M 242 248 Q 248 275 246 298 Q 240 312 230 314 Q 226 290 230 268 Z"
      />

      {/* ABS — slimmer rectangle for female */}
      <path
        {...m("abs")}
        d="M 130 200 Q 150 196 170 200 L 168 280 Q 158 290 150 290 Q 142 290 132 280 Z"
      />
      <g pointerEvents="none" stroke="#000" strokeWidth="0.8" opacity="0.4">
        <line x1="150" y1="208" x2="150" y2="280" />
        <line x1="135" y1="232" x2="165" y2="232" />
        <line x1="135" y1="256" x2="165" y2="256" />
      </g>

      {/* QUADS — wider for female hips */}
      <path
        {...m("quads")}
        d="M 100 365 Q 86 400 90 440 Q 100 470 115 472 Q 125 462 128 425 Q 126 388 118 366 Z"
      />
      <path
        {...m("quads")}
        d="M 200 365 Q 214 400 210 440 Q 200 470 185 472 Q 175 462 172 425 Q 174 388 182 366 Z"
      />

      {/* CALVES */}
      <path
        {...m("calves")}
        d="M 112 472 Q 100 510 106 552 Q 116 595 125 600 Q 130 555 128 510 Q 124 482 118 472 Z"
      />
      <path
        {...m("calves")}
        d="M 188 472 Q 200 510 194 552 Q 184 595 175 600 Q 170 555 172 510 Q 176 482 182 472 Z"
      />

      <HoverLabel id={hover} />
    </svg>
  );
}

/* --------------- FEMALE BACK --------------- */
export function FemaleBack(props) {
  const { hover } = props;
  const m = (id) => muscleProps({ ...props, id, view: "back" });

  return (
    <svg viewBox="0 0 300 620" className="w-full h-auto">
      <path
        fill="#0A0A0A"
        stroke="#D4AF37"
        strokeWidth="2.5"
        strokeLinejoin="round"
        d="M 150 12
           C 175 12 192 30 192 58
           C 192 78 184 96 174 102
           L 174 112
           C 188 116 205 122 220 134
           C 235 146 244 164 246 188
           C 247 205 242 222 234 232
           C 228 240 220 244 212 244
           L 214 285
           C 214 305 210 318 202 324
           C 196 330 188 332 182 330
           L 178 314
           L 175 340
           Q 175 355 188 365
           Q 215 375 220 405
           Q 218 442 210 480
           Q 200 510 197 545
           Q 195 580 200 605
           L 175 605
           L 165 470
           L 155 605
           L 145 605
           L 135 605
           L 125 470
           L 115 605
           L 90 605
           Q 95 580 93 545
           Q 90 510 80 480
           Q 72 442 70 405
           Q 75 375 102 365
           Q 115 355 115 340
           L 112 314
           L 108 330
           C 102 332 94 330 88 324
           C 80 318 76 305 76 285
           L 78 244
           C 70 244 62 240 56 232
           C 48 222 43 205 44 188
           C 46 164 55 146 70 134
           C 85 122 102 116 116 112
           L 116 102
           C 106 96 98 78 98 58
           C 98 30 125 12 150 12 Z"
      />

      {/* TRAPS */}
      <path
        {...m("traps")}
        d="M 150 108 L 195 130 Q 175 162 150 165 Q 125 162 105 130 Z"
      />

      {/* REAR DELTS */}
      <path
        {...m("shoulders")}
        d="M 117 118 Q 98 124 82 138 Q 72 150 70 168 Q 82 158 102 152 Q 114 146 122 140 Z"
      />
      <path
        {...m("shoulders")}
        d="M 183 118 Q 202 124 218 138 Q 228 150 230 168 Q 218 158 198 152 Q 186 146 178 140 Z"
      />

      {/* LATS — narrower V-taper */}
      <path
        {...m("lats")}
        d="M 128 145 Q 112 175 110 215 Q 112 250 130 280 L 148 290 L 148 145 Z"
      />
      <path
        {...m("lats")}
        d="M 172 145 Q 188 175 190 215 Q 188 250 170 280 L 152 290 L 152 145 Z"
      />

      {/* TRICEPS */}
      <path
        {...m("triceps")}
        d="M 60 175 Q 50 195 48 220 Q 50 238 58 248 Q 66 238 68 218 Q 68 196 64 180 Z"
      />
      <path
        {...m("triceps")}
        d="M 240 175 Q 250 195 252 220 Q 250 238 242 248 Q 234 238 232 218 Q 232 196 236 180 Z"
      />

      {/* FOREARMS */}
      <path
        {...m("forearms")}
        d="M 58 248 Q 52 275 54 298 Q 60 312 70 314 Q 74 290 70 268 Z"
      />
      <path
        {...m("forearms")}
        d="M 242 248 Q 248 275 246 298 Q 240 312 230 314 Q 226 290 230 268 Z"
      />

      {/* LOWER BACK */}
      <path
        {...m("lower-back")}
        d="M 128 278 Q 150 282 172 278 L 168 312 Q 150 318 132 312 Z"
      />

      {/* GLUTES — bigger for female */}
      <path
        {...m("glutes")}
        d="M 150 320 Q 115 322 95 345 Q 92 378 112 392 Q 138 396 150 380 Z"
      />
      <path
        {...m("glutes")}
        d="M 150 320 Q 185 322 205 345 Q 208 378 188 392 Q 162 396 150 380 Z"
      />

      {/* HAMSTRINGS */}
      <path
        {...m("hamstrings")}
        d="M 105 395 Q 92 425 92 455 Q 100 468 115 472 Q 125 462 128 425 Q 126 400 122 392 Z"
      />
      <path
        {...m("hamstrings")}
        d="M 195 395 Q 208 425 208 455 Q 200 468 185 472 Q 175 462 172 425 Q 174 400 178 392 Z"
      />

      {/* CALVES */}
      <path
        {...m("calves")}
        d="M 112 472 Q 100 510 106 552 Q 116 595 125 600 Q 130 555 128 510 Q 124 482 118 472 Z"
      />
      <path
        {...m("calves")}
        d="M 188 472 Q 200 510 194 552 Q 184 595 175 600 Q 170 555 172 510 Q 176 482 182 472 Z"
      />

      <HoverLabel id={hover} />
    </svg>
  );
}

export default function BodyDiagram({ gender, view, weekly, onPick, hover, setHover, heatColor }) {
  const props = { weekly, hover, setHover, onPick, heatColor };
  if (gender === "female") {
    return view === "front" ? <FemaleFront {...props} /> : <FemaleBack {...props} />;
  }
  return view === "front" ? <MaleFront {...props} /> : <MaleBack {...props} />;
}
